import { IEmailTemplate } from "easy-email-editor";
import { JsonToMjml } from "easy-email-core";
import { MjmlToJson } from "easy-email-extensions";
import mjml2html from "mjml-browser";
import { Message } from "@arco-design/web-react";
import { Uploader } from "./Uploaders";

// Function to import JSON template
export const onImportJSON = async ({
  restart,
}: {
  restart: (val: IEmailTemplate) => void;
}) => {
  const uploader = new Uploader(() => Promise.resolve(""), {
    accept: "application/json",
    limit: 1,
  });
  try {
    const [file] = await uploader.chooseFile();
    const reader = new FileReader();
    const emailTemplate = await new Promise<IEmailTemplate>(
      (resolve, reject) => {
        reader.onload = function (evt) {
          if (!evt.target) {
            reject(new Error("Failed to read file"));
            return;
          }
          try {
            const template = JSON.parse(
              evt.target.result as string
            ) as IEmailTemplate;
            resolve(template);
          } catch (error) {
            reject(error);
          }
        };
        reader.readAsText(file);
      }
    );

    restart(emailTemplate);
  } catch (error) {
    console.error("Error importing JSON:", error);
    Message.error("Failed to import JSON template");
  }
};

// Function to import MJML template
export const onImportMJML = async ({
  restart,
}: {
  restart: (val: IEmailTemplate) => void;
}) => {
  const uploader = new Uploader(() => Promise.resolve(""), {
    accept: "text/mjml",
    limit: 1,
  });

  try {
    const [file] = await uploader.chooseFile();
    const reader = new FileReader();
    const pageData = await new Promise<[string, IEmailTemplate["content"]]>(
      (resolve, reject) => {
        reader.onload = function (evt) {
          if (!evt.target) {
            reject(new Error("Failed to read file"));
            return;
          }
          try {
            const pageData = MjmlToJson(evt.target.result as string);
            resolve([file.name, pageData]);
          } catch (error) {
            reject(error);
          }
        };
        reader.readAsText(file);
      }
    );

    restart({
      subject: pageData[0],
      content: pageData[1],
      subTitle: "",
    });
  } catch (error) {
    console.error("Error importing MJML:", error);
    Message.error("Failed to import MJML template");
  }
};

// Function to import HTML template
export const onImportHTML = async ({
  restart,
}: {
  restart: (val: IEmailTemplate) => void;
}) => {
  const uploader = new Uploader(() => Promise.resolve(""), {
    accept: "text/html",
    limit: 1,
  });

  try {
    const [file] = await uploader.chooseFile();
    const reader = new FileReader();

    reader.onload = async function (evt) {
      if (!evt.target) {
        Message.error("Failed to read file");
        return;
      }

      try {
        // Get the HTML content
        const htmlContent = evt.target.result as string;

        // Convert HTML to MJML using the html2mjml function
        const mjmlContent = await html2mjml(htmlContent);

        // Convert MJML to JSON format that the editor expects
        const pageData = MjmlToJson(mjmlContent);

        // Restart the editor with the new template
        restart({
          subject: file.name.replace(".html", ""),
          content: pageData,
          subTitle: "",
        });

        Message.success("HTML imported successfully!");
      } catch (error) {
        console.error("Error processing HTML:", error);
        Message.error("Failed to import HTML. Check console for details.");
      }
    };

    reader.readAsText(file);
  } catch (error) {
    console.error("Error importing HTML:", error);
    Message.error("Failed to import HTML template");
  }
};

// Helper function to convert HTML to MJML
// This uses a simplified approach - for production, consider using a more robust HTML to MJML conversion
const html2mjml = async (htmlContent: string): Promise<string> => {
  // Create a more structured MJML template that preserves content editability
  // This approach creates a basic structure that the editor can work with
  const mjmlTemplate = `
<mjml>
  <mj-head>
    <mj-title>Imported HTML Template</mj-title>
    <mj-attributes>
      <mj-all font-family="Arial, sans-serif" />
      <mj-text font-size="16px" color="#333333" line-height="1.5" />
    </mj-attributes>
  </mj-head>
  <mj-body>
    <mj-section>
      <mj-column>
        <mj-text>
          ${sanitizeHtml(htmlContent)}
        </mj-text>
      </mj-column>
    </mj-section>
  </mj-body>
</mjml>
  `;

  return mjmlTemplate;
};

// Helper function to sanitize HTML for MJML compatibility
const sanitizeHtml = (html: string): string => {
  // This is a basic sanitization
  // Remove doctype, html, head, body tags
  let sanitized = html
    .replace(/<\!DOCTYPE[^>]*>/i, "")
    .replace(/<html[^>]*>([\s\S]*?)<\/html>/i, "$1")
    .replace(/<head[^>]*>([\s\S]*?)<\/head>/i, "")
    .replace(/<body[^>]*>([\s\S]*?)<\/body>/i, "$1");

  // Make all self-closing tags XML compliant for MJML
  sanitized = sanitized.replace(/<(meta|link|img|br|hr)([^>]*)>/gi, "<$1$2 />");

  return sanitized;
};

// Function to export HTML
export const onExportHTML = (values: IEmailTemplate) => {
  try {
    const mjmlContent = JsonToMjml({
      data: values.content,
      mode: "production",
      context: values.content,
    });

    const { html, errors } = mjml2html(mjmlContent);

    if (errors.length) {
      console.error("MJML errors:", errors);
      Message.error("Error generating HTML. Check console for details.");
      return;
    }

    const blob = new Blob([html], { type: "text/html" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "email-template.html";
    link.click();
    Message.success("HTML Exported Successfully!");
  } catch (error) {
    console.error("Error exporting HTML:", error);
    Message.error("Failed to export HTML. Check console for details.");
  }
};

// Function to export JSON
export const onExportJSON = (values: IEmailTemplate) => {
  const jsonString = JSON.stringify(values, null, 2);
  const blob = new Blob([jsonString], { type: "application/json" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = "email-template.json";
  link.click();
  Message.success("JSON Exported Successfully!");
};

// Function to export MJML
export const onExportMJML = (values: IEmailTemplate) => {
  const mjmlString = JsonToMjml({
    data: values.content,
    mode: "production",
    context: values.content,
  });

  const blob = new Blob([mjmlString], { type: "text/xml" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = "email-template.mjml";
  link.click();
  Message.success("MJML Exported Successfully!");
};
